import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container, Grid, TextField, Typography } from '@mui/material';
import DatePicker from '@mui/lab/DatePicker';
// import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import useSettings from '../../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { dispatch, useSelector } from '../../../../redux/store';
import { getStockClosingHistory, getStockClosingHistoryRequestInit, getStockClosingHistoryStreamUrl } from '../../../../redux/slices/stockManagementRedux/stockRedux';
// import { downloadStockClosingHistory } from '../../../../redux/slices/stockManagementRedux/stockRedux';
// import { getAllFoods } from '../../../../redux/slices/stockManagementRedux/foodRedux';
import { fCurrency } from '../../../../utils/formatNumber';
import { utcMoment } from '../../../../utils/common';
import { readParquetObjectsFromUrl } from '../../../../utils/parquetReader';

const getDefaultBusinessDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date;
};

const formatDate = (date) => format(date, 'yyyy-MM-dd');

const ClosingStockHistory = () => {
  const { themeStretch } = useSettings();
  const [businessDate, setBusinessDate] = useState(getDefaultBusinessDate());
  const [stockCode, setStockCode] = useState('');
  // const [selectedFood, setSelectedFood] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rawRows, setRawRows] = useState([]);
  const [snapshotStatus, setSnapshotStatus] = useState(null);

  const { selectedLocation } = useSelector((state) => state.location);
  const selectedReportLocation = selectedLocation;
  // const { allData: foods } = useSelector((state) => state.food);

  // useEffect(() => {
  //   dispatch(getAllFoods());
  // }, []);

  useEffect(() => {
    if (!businessDate) return;

    setRawRows([]);
    setSnapshotStatus(null);
    setIsLoading(true);
    dispatch(
      getStockClosingHistory({
        businessDate: formatDate(businessDate),
      })
    )
      .then(async (response) => {
        if (!response?.status || !response?.metadata) {
          setSnapshotStatus(response?.message || 'Snapshot is not available for this date/location.');
          return;
        }

        const rows = await readParquetObjectsFromUrl(getStockClosingHistoryStreamUrl({ businessDate: formatDate(businessDate) }), {
          byteLength: Number(response?.metadata?.fileSize || 0) || undefined,
          requestInit: getStockClosingHistoryRequestInit(),
        });
        setRawRows(rows || []);
      })
      .catch((error) => {
        const message = error?.message || 'Could not load stock closing history.';
        setSnapshotStatus(message);
        toast.error(message, { position: 'bottom-right', autoClose: false });
      })
      .finally(() => setIsLoading(false));
  }, [businessDate]);

  const filteredRows = useMemo(() => {
    const selectedLocationId = selectedReportLocation?.id ? String(selectedReportLocation.id) : '';
    const searchCode = String(stockCode || '').trim().toLowerCase();
    // const selectedFoodId = selectedFood?.id ? String(selectedFood.id) : '';

    return (rawRows || []).filter((row) => {
      if (selectedLocationId && selectedLocationId !== '1' && String(row.locationId) !== selectedLocationId) return false;
      if (searchCode && !String(row.stockCode || '').toLowerCase().includes(searchCode)) return false;
      // if (selectedFoodId && String(row.foodId) !== selectedFoodId) return false;
      return true;
    });
  }, [rawRows, selectedReportLocation?.id, stockCode]);

  const tableData = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        closingAt: row.closingAt ? utcMoment(row.closingAt).format('YYYY-MM-DD HH:mm:ss') : '-',
        expireDate: row.expireDate ? utcMoment(row.expireDate).format('YYYY-MM-DD') : '-',
        initialQty: Number(row.initialQty || 0),
        currentQty: Number(row.currentQty || 0),
        costPerUnit: Number(row.costPerUnit || 0),
        stockValue: Number(row.stockValue || 0),
      })),
    [filteredRows]
  );

  const columns = [
    { accessorKey: 'stockCode', header: 'Stock Code' },
    { accessorKey: 'foodName', header: 'Food' },
    { accessorKey: 'locationName', header: 'Location' },
    { accessorKey: 'stockStatusName', header: 'Status' },
    { accessorKey: 'currentQty', header: 'Current Qty' },
    { accessorKey: 'unitSymbol', header: 'Unit' },
    { accessorKey: 'costPerUnit', header: 'Cost/Unit', Cell: ({ row }) => fCurrency(row.original.costPerUnit || 0) },
    { accessorKey: 'stockValue', header: 'Stock Value', Cell: ({ row }) => fCurrency(row.original.stockValue || 0) },
    { accessorKey: 'expireDate', header: 'Expire Date' },
    { accessorKey: 'orderId', header: 'GRN ID' },
    { accessorKey: 'parentId', header: 'Parent ID' },
  ];

  // const handleDownload = async () => {
  //   try {
  //     await downloadStockClosingHistory({ businessDate: formatDate(businessDate) });
  //   } catch (error) {
  //     toast.error(error?.message || 'Could not download stock closing history.', { position: 'bottom-right', autoClose: false });
  //   }
  // };

  return (
    <Page title='Closing Available Stock History'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Closing Available Stock History'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Reports', href: PATH_DASHBOARD.reports.root },
            { name: 'Stock Reports', href: PATH_DASHBOARD.reports.stock.root },
            { name: 'Closing Available Stock History' },
          ]}
          action={[
            // <Button key='download' variant='contained' startIcon={<FileDownloadIcon />} disabled={!selectedReportLocation?.id || !businessDate} onClick={handleDownload}>
            //   Download Parquet
            // </Button>,
          ]}
        />

        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <DatePicker label='Business date' value={businessDate} onChange={(newValue) => newValue && setBusinessDate(newValue)} renderInput={(params) => <TextField {...params} fullWidth />} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label='Location' value={selectedReportLocation?.name || ''} fullWidth disabled />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label='Stock code' value={stockCode} onChange={(event) => setStockCode(event.target.value)} fullWidth />
          </Grid>
          {/* <Grid item xs={12} md={3}>
            <Autocomplete
              options={foods || []}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={selectedFood}
              onChange={(event, value) => setSelectedFood(value)}
              renderInput={(params) => <TextField {...params} label='Food' fullWidth />}
            />
          </Grid> */}
        </Grid>

        {snapshotStatus && (
          <Card sx={{ p: 2, mb: 2 }}>
            <Typography color='text.secondary'>{snapshotStatus}</Typography>
          </Card>
        )}

        <DataGridTable
          name={`Closing Available Stock History - ${businessDate ? formatDate(businessDate) : ''} - ${selectedReportLocation?.name || ''}`}
          data={tableData}
          column={columns}
          isLoading={isLoading}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          enableRowActions={false}
          enableRowSelection={false}
          isRowClickable={false}
          rowsPerPage={100}
          renderRowActionItems={() => []}
        />
      </Container>
    </Page>
  );
};

export default ClosingStockHistory;
